import { createServerFn } from '@tanstack/react-start'
import { getRequestHeader } from '@tanstack/react-start/server'
import { authMiddleware } from './auth'
import { db } from '../lib/db'
import { Query } from 'node-appwrite'
import z from 'zod'
import { llmEnum, type Deployments } from '../lib/appwrite.types'
import { startCreditsCount } from '../lib/constants'

const BACKEND_URL = process.env.BACKEND_URL

const createDeploymentSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50),
  llmModel: llmEnum,
  telegramToken: z.string().optional(),
})

export const createDeploymentFn = createServerFn({ method: 'POST' })
  .inputValidator(createDeploymentSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()
    if (!currentUser) throw new Error('Unauthorized')

    const creditCost = startCreditsCount
    const credits = currentUser.prefs?.credits ?? 0
    if (credits < creditCost) {
      throw new Error(
        `Insufficient AI credits. You need ${creditCost} credits to deploy. Please upgrade your plan or top up.`,
      )
    }
    if (!currentUser.prefs.plan) {
      throw new Error('Please upgrade your plan to deploy.')
    }
    const startTime = Date.now()
    console.log('Flipping the switch: Triggering deployment...')


    try {
      const response = await fetch(`${BACKEND_URL}/api/deployments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Cookie: getRequestHeader('cookie') || '',
          Authorization: getRequestHeader('authorization') || '',
        },
        body: JSON.stringify(data),
      })

      const duration = Date.now() - startTime
      console.log(`Backend responded in ${duration}ms`)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Express Backend Error:', errorText)
        throw new Error(
          errorText || 'Failed to create deployment. Please check the backend status.',
        )
      }

      return await response.json()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const duration = Date.now() - startTime
      if (error.name === 'AbortError') {
        console.error(`Request timed out after ${duration}ms`)
        throw new Error(
          'The deployment server is taking too long to respond. Please check the backend status.',
        )
      }
      console.error(`Request failed after ${duration}ms:`, error)
      throw error
    }
  })

export const streamDeploymentLogsFn = createServerFn({ method: 'GET' })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    try {
      const { currentUser } = await authMiddleware()
      if (!currentUser) throw new Error('Unauthorized')

      const deployment = await db.deployments.get(data.id)

      // Verify ownership
      if (deployment.createdBy !== currentUser.$id) {
        throw new Error('Unauthorized')
      }

      return deployment
    } catch (error) {
      console.log('Error Streaming deployment', error)

      return null
    }
  })

export const verifyDeploymentFn = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ id: z.string(), code: z.string() }))
  .handler(async ({ data }) => {
    const response = await fetch(
      `${BACKEND_URL}/api/deployments/${data.id}/verify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Cookie: getRequestHeader('cookie') || '',
          Authorization: getRequestHeader('authorization') || '',
        },
        body: JSON.stringify({ code: data.code }),
      },
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.error || 'Failed to verify pairing code')
    }

    return await response.json()
  })

// List user's deployments
export const listDeploymentsFn = createServerFn({ method: 'GET' }).handler(
  async (): Promise<{ deployments: Deployments[] }> => {
    const { currentUser } = await authMiddleware()
    if (!currentUser) throw new Error('Unauthorized')

    const result = await db.deployments.list([
      Query.equal('createdBy', [currentUser.$id]),
      Query.orderDesc('$createdAt'),
    ])

    return { deployments: result.rows }
  },
)

export const getUserCreditsFn = createServerFn({ method: 'POST' })
  .inputValidator(z.object({ userId: z.string() }))
  .handler(async ({ data }) => {
    const currentUser = await db.user.get(data.userId)
    let remaining = currentUser.prefs.credits || currentUser.prefs.plan === 'starter' ? 1500 : 4500

    // Get all deployments for the user
    const deploymentsResult = await db.deployments.list([
      Query.equal('createdBy', [currentUser.$id]),
    ])
    const deployments = deploymentsResult.rows

    // Deduct server cost: 1275 credits per deployment
    remaining -= startCreditsCount * deployments.length

    // Deduct AI usage from OpenRouter
    for (const deployment of deployments) {
      if (deployment.apiKey) {
        try {
          const resp = await fetch('https://openrouter.ai/api/v1/key', {
            headers: {
              Authorization: `Bearer ${deployment.apiKey}`,
            },
          })
          if (resp.ok) {
            const json = await resp.json()
            const usd = json.data?.usage || 0
            console.log({ usd, json });

            const creditsToDeduct = Math.round(usd * 52) // $1 = 52 credits
            remaining -= creditsToDeduct
          }
        } catch (e) {
          console.error(
            'OpenRouter usage fetch error for deployment',
            deployment.$id,
            e,
          )
        }
      }
    }

    // Ensure non-negative
    remaining = Math.max(0, remaining)

    // Update profile with final remaining credits
    // await db.user.updatePrefs(currentUser.$id, { credits: remaining })

    return remaining
  })

const deleteDeploymentSchema = z.object({
  id: z.string().min(1),
})

export const deleteDeploymentFn = createServerFn({ method: 'POST' })
  .inputValidator(deleteDeploymentSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()
    if (!currentUser) throw new Error('Unauthorized')

    const deployment = await db.deployments.get(data.id)

    // Verify ownership
    if (deployment.createdBy !== currentUser.$id) {
      throw new Error('Unauthorized')
    }

    // Call backend API to delete (handles GCP instance + DB record)
    const response = await fetch(`${BACKEND_URL}/api/deployments/${data.id}`, {
      method: 'DELETE',
      headers: {
        Cookie: getRequestHeader('cookie') || '',
        Authorization: getRequestHeader('authorization') || '',
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to delete deployment')
    }

    return { success: true }
  })
